import { ForwardRefRenderFunction, forwardRef, useCallback, useContext, useImperativeHandle } from 'react'

import { useDisclosure } from '@chakra-ui/react'
import { UserContext } from '@editor/contexts/Users'
import { EditorDisplayConfig } from '@editor/interfaces'
import { InspectionArea } from '@editor/interfaces/inspection'
import { setIsVisible } from '@editor/pages/projects/common/SolidMeshManager/store'
import { patchInspectionArea } from '@editor/services/InspectionArea'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { patchInspectionAreas, setIsSavingArea } from '@editor/store/page'
import {
  setCurrentAdjustingKey,
  setCurrentFillColor,
  setCurrentLabelBackgroundColor,
  setCurrentLabelTextColor,
} from '@editor/store/tool'
import { toaster } from '@editor/system/toaster'
import { EditorSettingsDrawer } from '@infraloop/common'
import * as Sentry from '@sentry/react'

import { DefaultEditorDisplayConfig } from '../../constants'
import { setIsPcdVisible } from '../../store/editor'
import { patchEditorDisplaySettings, setDisplaySettings } from '../../store/editorSettings'

export interface SettingsDrawerRef {
  openDrawer: () => void
}

/**
 * Recursively go through display settings and see if it contains the specified key.
 *
 * @param data The display settings data
 * @param keyToCheck The key to check for existence
 * @param currentPath The current path in recursion
 * @returns The path and value if found, null otherwise
 */
const hasValueChanged = (
  data: Record<string, unknown>,
  keyToCheck: string,
  currentPath: string[] = [],
): { path: string; value: string } | null => {
  const keys = Object.keys(data)
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i]
    if (key === keyToCheck && typeof data[key] === 'string') {
      return { path: [...currentPath, key].join('.'), value: data[key] }
    }

    const value = data[key] as Record<string, unknown>
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const result = hasValueChanged(value, keyToCheck, [...currentPath, key])
      if (result) return result
    }
  }

  return null
}

const SettingsDrawerComponent: ForwardRefRenderFunction<SettingsDrawerRef> = (props, ref) => {
  const { open, onOpen, onClose } = useDisclosure()

  // Context
  const { getAccessToken } = useContext(UserContext)

  // Store
  const dispatch = useAppDispatch()
  const userProfile = useAppSelector((state) => state.user.userProfile)
  const project = useAppSelector((state) => state.page.project)
  const inspectionArea = useAppSelector((state) => state.page.inspectionArea)
  const editorDisplaySettings = useAppSelector((state) => state.editorSettings.display)
  const isSavingArea = useAppSelector((state) => state.page.isSavingArea)

  /**
   * Handle finalized settings. Applied to the live settings state, so the scene previews the
   * change before it is saved.
   * @param newSettings The new settings
   */
  const handleOnUpdated = useCallback(
    (newSettings: RecursivePartial<EditorDisplayConfig>) => {
      dispatch(patchEditorDisplaySettings(newSettings))
      dispatch(setCurrentFillColor(undefined))
      dispatch(setCurrentAdjustingKey(undefined))

      // If pcd or mesh visibility changed, reset local values
      if (newSettings.pcd?.visible !== undefined) dispatch(setIsPcdVisible(newSettings.pcd.visible))
      if (newSettings.mesh?.visible !== undefined) dispatch(setIsVisible(newSettings.mesh.visible))
    },
    [dispatch],
  )

  /**
   * Handle real-time change of settings
   * @param newSettings The new settings
   */
  const handleRealTimeChange = useCallback(
    (newSettings: RecursivePartial<EditorDisplayConfig>) => {
      // Unlabeled color changed
      if (newSettings.general?.unlabeled_color) {
        dispatch(setCurrentAdjustingKey('general.unlabeled_color'))
        dispatch(setCurrentFillColor(newSettings.general.unlabeled_color))
        return
      }

      // Primary color changed
      const color = hasValueChanged(newSettings, 'color')
      if (color) {
        dispatch(setCurrentAdjustingKey(color.path))
        dispatch(setCurrentFillColor(color.value))
        return
      }

      // Label background color changed
      const bgColor = hasValueChanged(newSettings, 'bg_color')
      if (bgColor) {
        dispatch(setCurrentAdjustingKey(bgColor.path))
        dispatch(setCurrentLabelBackgroundColor(bgColor.value))
        return
      }

      // Label text color changed
      const textColor = hasValueChanged(newSettings, 'text_color')
      if (textColor) {
        dispatch(setCurrentAdjustingKey(textColor.path))
        dispatch(setCurrentLabelTextColor(textColor.value))
      }
    },
    [dispatch],
  )

  /**
   * Handles canceling changes and closing the drawer.
   */
  const handleCancel = useCallback(() => {
    if (!inspectionArea) return

    onClose()
    dispatch(setDisplaySettings(inspectionArea.display_config || DefaultEditorDisplayConfig))
  }, [onClose, dispatch, inspectionArea])

  /**
   * Handle save settings
   */
  const handleSave = useCallback(async () => {
    if (!inspectionArea || !project) return

    dispatch(setIsSavingArea(true))

    try {
      const token = await getAccessToken()
      if (!token) return

      const result = await patchInspectionArea(token, project.project_id, {
        ...inspectionArea,
        display_config: editorDisplaySettings,
      } as InspectionArea)
      if (!result) return

      dispatch(patchInspectionAreas([result]))
      toaster.create({
        description: '3D画面設定が正常に保存されました',
        type: 'success',
      })
      onClose()
    } catch (err) {
      Sentry.captureException(err)
    } finally {
      dispatch(setIsSavingArea(false))
    }
  }, [project, inspectionArea, editorDisplaySettings, dispatch, getAccessToken, onClose])

  /**
   * Reset settings to account settings.
   */
  const handleResetToAccountSettings = useCallback(() => {
    if (!userProfile) return
    dispatch(setDisplaySettings(userProfile.display_config))
  }, [userProfile, dispatch])

  /**
   * Open the drawer
   */
  const openDrawer = useCallback(() => {
    onOpen()
  }, [onOpen])

  useImperativeHandle(ref, () => ({ openDrawer }))

  return (
    <EditorSettingsDrawer
      open={open}
      settings={editorDisplaySettings}
      isSaving={isSavingArea}
      onChange={handleRealTimeChange}
      onUpdated={handleOnUpdated}
      onSave={() => {
        handleSave().catch(() => {})
      }}
      onCancel={handleCancel}
      onResetToAccountSettings={handleResetToAccountSettings}
    />
  )
}

// Note: forwardRef is from react, not chakra-ui
const SettingsDrawer = forwardRef(SettingsDrawerComponent)
export default SettingsDrawer
